import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { ShieldCheck, Loader2, Eye, EyeOff } from "lucide-react";
import { Toaster, toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/mockData";

export default function Login() {
  const { signIn, loading } = useAuth();
  const [email, setEmail] = useState("editor@yoobikwitus.com");
  const [password, setPassword] = useState("demo1234");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await signIn(email.trim(), password);
      toast.success("Signed in", { description: "Welcome to Yoobikwitus." });
      const to = location.state?.from?.pathname || "/dashboard";
      navigate(to, { replace: true });
    } catch (err) {
      setError(err.message || "Invalid credentials");
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#0A0A0B] text-zinc-100 grid lg:grid-cols-2">
      {/* Left — brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 border-r border-[#272A30] overflow-hidden">
        <div className="absolute inset-0 grid-noise opacity-60 pointer-events-none" />
        <div className="relative flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-white/5 ring-1 ring-white/10 flex items-center justify-center overflow-hidden">
            <img src={LOGO_URL} alt="Yoobikwitus" className="h-7 w-7 object-contain" />
          </div>
          <div>
            <div className="text-sm font-semibold tracking-tight">Yoobikwitus</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
              yoobikwitus.com
            </div>
          </div>
        </div>

        <div className="relative max-w-md">
          <div className="text-xs uppercase tracking-[0.22em] text-zinc-500 mb-4">
            Newsroom verification suite
          </div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-balance leading-[1.05]">
            Forensic confidence,
            <br />
            in seconds.
          </h1>
          <p className="mt-5 text-zinc-400 text-base leading-relaxed">
            Verify provenance, surface AI-generated imagery, and clear assets for
            publication before the deadline. Built for picture desks under pressure.
          </p>

          <div className="mt-10 grid grid-cols-3 gap-3 text-[11px] uppercase tracking-wider text-zinc-500">
            <div className="rounded-lg border border-[#272A30] bg-[#111318] px-3 py-3">
              <div className="text-emerald-400 text-base font-semibold tracking-tight normal-case">
                &gt;70
              </div>
              Likely genuine
            </div>
            <div className="rounded-lg border border-[#272A30] bg-[#111318] px-3 py-3">
              <div className="text-amber-400 text-base font-semibold tracking-tight normal-case">
                40–70
              </div>
              Inconclusive
            </div>
            <div className="rounded-lg border border-[#272A30] bg-[#111318] px-3 py-3">
              <div className="text-red-400 text-base font-semibold tracking-tight normal-case">
                &lt;40
              </div>
              Likely AI
            </div>
          </div>
        </div>

        <div className="relative text-[11px] uppercase tracking-[0.18em] text-zinc-600">
          © Yoobikwitus · Editorial verification
        </div>
      </div>

      {/* Right — form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-md space-y-6"
          data-testid="login-form"
        >
          <div>
            <div className="lg:hidden mb-6 flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-white/5 ring-1 ring-white/10 flex items-center justify-center overflow-hidden">
                <img src={LOGO_URL} alt="Yoobikwitus" className="h-7 w-7 object-contain" />
              </div>
              <span className="font-semibold tracking-tight">Yoobikwitus</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">
              Sign in to your desk
            </h2>
            <p className="mt-2 text-sm text-zinc-400">
              Use your newsroom credentials. Sessions are held in memory only.
            </p>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <label
                htmlFor="email"
                className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500"
              >
                Email
              </label>
              <input
                id="email"
                data-testid="login-email-input"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="editor@yoobikwitus.com"
                className="w-full rounded-lg bg-[#0A0A0B] border border-[#272A30] focus:border-zinc-500 focus:ring-1 focus:ring-zinc-500 outline-none px-3 py-2.5 text-sm placeholder:text-zinc-600 transition-colors"
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="password"
                className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500"
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  data-testid="login-password-input"
                  type={showPw ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full rounded-lg bg-[#0A0A0B] border border-[#272A30] focus:border-zinc-500 focus:ring-1 focus:ring-zinc-500 outline-none px-3 py-2.5 text-sm placeholder:text-zinc-600 transition-colors pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute inset-y-0 right-2 flex items-center text-zinc-500 hover:text-zinc-300"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  data-testid="login-toggle-password"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div
              role="alert"
              data-testid="login-error"
              className="rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm px-3 py-2"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-white text-[#0A0A0B] font-semibold text-sm px-4 py-2.5 hover:bg-zinc-200 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Signing in…
              </>
            ) : (
              <>
                <ShieldCheck className="h-4 w-4" /> Sign in
              </>
            )}
          </button>

          <div className="rounded-lg border border-[#272A30] bg-[#111318] px-3 py-2.5 text-[11px] text-zinc-500">
            <span className="uppercase tracking-[0.18em] mr-2 text-zinc-400">Demo</span>
            editor@yoobikwitus.com / demo1234
          </div>

          <p className="text-sm text-zinc-400 text-center" data-testid="login-signup-link">
            New here?{" "}
            <Link to="/signup" className="text-white underline underline-offset-4 hover:text-zinc-200">
              Create a free account
            </Link>
            <span className="text-zinc-600"> · one check a day, no card needed</span>
          </p>
        </form>
        <Toaster
          theme="dark"
          position="bottom-right"
          toastOptions={{
            classNames: {
              toast:
                "!bg-[#111318] !border !border-[#272A30] !text-zinc-100 !rounded-xl !shadow-[0_8px_32px_-8px_rgba(0,0,0,0.6)]",
            },
          }}
        />
      </div>
    </div>
  );
}
